import { DownloadOutlined, FileOutlined } from "@ant-design/icons"
import { Alert, App, Button, Card, Flex, Layout, Result, Spin, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { notificationOptions } from "../../constants/notification.config"
import { fetchFileBlob, saveBlob } from "../../utils/download-file"
import { fileFormatLabel } from "../Files/helpers/file-format-label"
import { useSharedFile } from "./hooks/use-shared-file"
import type { SharedFile } from "./types/shared-file.types"

const { Title, Text } = Typography

const FileMeta: React.FC<{ file: SharedFile }> = ({ file }) => {
  const { t } = useTranslation()
  const type = fileFormatLabel({ name: file.file_name }) ?? t("files.columns.typeDocument")
  const size = file.formatted_file_size

  return <Text type="secondary">{size ? t("sharedFile.meta", { type, size }) : type}</Text>
}

const DownloadButton: React.FC<{ url: string; name: string }> = ({ url, name }) => {
  const { t } = useTranslation()
  const { message, notification } = App.useApp()
  const [downloading, setDownloading] = useState(false)

  const onDownload = async () => {
    setDownloading(true)
    try {
      saveBlob(await fetchFileBlob(url), name)
      void message.success(t("sharedFile.downloaded", { name }))
    } catch {
      notification.error({
        ...notificationOptions.error,
        message: t("sharedFile.downloadFailed"),
        duration: 0
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Button
      type="primary"
      size="large"
      block
      icon={<DownloadOutlined />}
      loading={downloading}
      onClick={() => void onDownload()}
    >
      {t("sharedFile.download")}
    </Button>
  )
}

const SharedFileCard: React.FC<{ file: SharedFile }> = ({ file }) => {
  const { t } = useTranslation()
  const url = file.download_url

  return (
    <Flex vertical gap="middle">
      <Text type="secondary">{t("sharedFile.sharedWithYou")}</Text>
      <Flex gap="small" align="center">
        <FileOutlined className="text-3xl" aria-hidden />
        <Flex vertical className="min-w-0">
          <Title level={4} className="!m-0 break-words">
            {file.file_name}
          </Title>
          <FileMeta file={file} />
        </Flex>
      </Flex>
      {url ? (
        <DownloadButton url={url} name={file.file_name} />
      ) : (
        <Alert type="info" showIcon message={t("sharedFile.downloadOff")} />
      )}
    </Flex>
  )
}

/** Where a document share link lands, for a visitor with or without an account. */
const SharedFilePage: React.FC = () => {
  const { t } = useTranslation()
  const { token = "" } = useParams<{ token: string }>()
  const { state, retry } = useSharedFile(token)

  return (
    <Layout className="min-h-svh auth-bg">
      <Flex vertical align="center" justify="center" className="min-h-svh p-4">
        <Card className="w-full max-w-lg">
          {state.kind === "loading" && (
            <Flex vertical align="center" gap="middle" className="py-8">
              <Spin size="large" />
              <Text type="secondary">{t("sharedFile.loading")}</Text>
            </Flex>
          )}

          {state.kind === "file" && <SharedFileCard file={state.file} />}

          {state.kind === "gone" && (
            <Result
              status="warning"
              title={t("sharedFile.gone.title")}
              subTitle={t("sharedFile.gone.detail")}
            />
          )}

          {state.kind === "error" && (
            <Result
              status="error"
              title={t("sharedFile.error.title")}
              subTitle={t("sharedFile.error.detail")}
              extra={
                <Button type="primary" onClick={retry}>
                  {t("sharedFile.retry")}
                </Button>
              }
            />
          )}
        </Card>
      </Flex>
    </Layout>
  )
}

export default SharedFilePage
